/**
 * HTML sanitizer — strips dangerous tags/attributes to prevent XSS from LLM-generated content.
 * #17 fix: All dangerouslySetInnerHTML usage must go through this.
 */

const ALLOWED_TAGS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr',
  'ul', 'ol', 'li',
  'strong', 'em', 'b', 'i', 'u', 's', 'del', 'ins', 'mark',
  'a', 'code', 'pre', 'blockquote',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'figure', 'figcaption',
  'div', 'span', 'article', 'section',
  'sup', 'sub', 'small',
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
  code: new Set(['class']),
  pre: new Set(['class']),
  div: new Set(['class', 'style']),
  span: new Set(['class', 'style']),
};

// Only allow safe style properties
const SAFE_STYLE_PROPS = new Set([
  'color', 'background-color', 'background', 'font-size', 'font-weight',
  'text-align', 'margin', 'padding', 'border', 'border-radius',
  'max-width', 'max-height', 'width', 'height', 'display',
]);

function sanitizeStyleAttr(style: string): string {
  return style
    .split(';')
    .map(s => s.trim())
    .filter(s => {
      const prop = s.split(':')[0]?.trim().toLowerCase();
      return prop && SAFE_STYLE_PROPS.has(prop) && !s.includes('url(') && !s.includes('expression(');
    })
    .join('; ');
}

/**
 * Sanitize HTML string — remove dangerous tags and attributes.
 * Uses regex-based approach suitable for LLM-generated content (not adversarial HTML).
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  // Remove script, style, iframe, object, embed, form, input tags entirely
  let clean = html.replace(/<(script|style|iframe|object|embed|form|input|textarea|select|button|link|meta|base)[^>]*>[\s\S]*?<\/\1>/gi, '');
  clean = clean.replace(/<(script|style|iframe|object|embed|form|input|textarea|select|button|link|meta|base)[^>]*\/?>/gi, '');

  // Remove event handlers (onclick, onerror, onload, etc.)
  clean = clean.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '');

  // Remove javascript: and data: URIs in href/src
  clean = clean.replace(/(href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, '$1=""');
  clean = clean.replace(/(href|src)\s*=\s*(?:"data:(?!image\/)[^"]*"|'data:(?!image\/)[^']*')/gi, '$1=""');

  // Sanitize style attributes
  clean = clean.replace(/style\s*=\s*"([^"]*)"/gi, (_, style) => {
    const sanitized = sanitizeStyleAttr(style);
    return sanitized ? `style="${sanitized}"` : '';
  });

  return clean;
}
